/// <amd-module name="Repos/Views/Settings/Repositories/Components/RepositoryPermissions" />
import React = require("react");
import { IBranchesProps } from "Repos/Views/Settings/Branches/Components/Branches/Branches.Props";
import { SimpleGitRef } from "Repos/Views/Settings/Branches/Data/SimpleGitRef";
import { IPermissionsSet, IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories/Resources";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IRepositoriesViewService, permissionsAdditionalData } from "../Services/RepositoriesViewService";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { GitRepositoryPermissions } from "Repos/Common/Permissions";

interface IRepositoryPermissionsProps {
    createChildLayer?(): void;
}

export class RepositoryPermissions extends VssComponent<IRepositoryPermissionsProps> {
    private selected: IReadonlyObservableValue<IRepository | undefined>;
    private selectedRefs: IReadonlyObservableValue<string | undefined>;
    private svc: IRepositoriesViewService;
    private _hasRestrictPermission: ObservableValue<boolean>;
    private _hasOpenPermission: boolean;

    public constructor(props: IRepositoryPermissionsProps, context: IVssComponentContext) {
        super(props, context);
        this.svc = context.pageContext.getService<IRepositoriesViewService>("RepositoriesViewService");
        this.selected = this.svc.getSelected();
        this.selectedRefs = this.svc.getSelectedRefs();
        // repo specific permissions are missing from the initial shared data, global ones are added at initialization time of the permission service
        this._hasRestrictPermission = new ObservableValue(false);
        this.svc.checkPermissionsAsync(GitRepositoryPermissions.EditPolicies, this.selected.value?.id).then(hasPermission => {
            this._hasRestrictPermission.value = hasPermission;
        });
        this._hasOpenPermission = this.svc.checkPermissions(GitRepositoryPermissions.EditPolicies);    
    }

    public render = (): JSX.Element => {
        return (
            <div className="rhythm-vertical-20">
                <Observer selected={this.selected} selectedRefs={this.selectedRefs} hasRestrictPermission={this._hasRestrictPermission}>
                    {(props: { selected: IRepository | undefined; selectedRefs: string | undefined; hasRestrictPermission: boolean}) => {
                        const permissionSet = this.svc.getPermissionSet(props.selectedRefs);
                        return (
                            <>
                                <div className="repository-permissions">
                                    <WrappedComponent
                                        loadingComponent={renderLoading}
                                        dependencies={["ms.vss-admin-web.security-view-common-content"]}
                                        wrappedProps={getSecurityComponentProps(
                                            permissionSet,
                                            this.svc.getProject().id!,
                                            this.selected.value,
                                            this.selectedRefs.value
                                        )}
                                        wrappedType="ms.vss-admin-web.security-page-view-component"
                                    />
                                </div>
                                {this.selected.value && !this.selected.value.isTfvc && !this.selectedRefs.value && (
                                    <>
                                        {isFeatureFlagEnabled(this.context.pageContext, "Git.ProtectedRepos", false) && (
                                            <WrappedComponent 
                                                loadingComponent={renderLoading}
                                                dependencies={["ms.vss-distributedtask-web.dt-security"]}
                                                wrappedProps={{
                                                    resourceId: this.svc.getProject().id + '.' + props.selected!.id.toString(),
                                                    resourceType: "repository",
                                                    noAuthorizedPipelinesViewLearnMoreLink: "https://aka.ms/yamlauthz",
                                                    noRestrictionsViewLearnMoreLink: "https://aka.ms/yamlauthz",
                                                    canOpenAccess: this._hasOpenPermission,
                                                    canRestrictAccess: this._hasRestrictPermission.value,
                                                    customNoAuthorizedPipelinesCardContent: Resources.NoAuthorizedPipelinesCardContent
                                                }}
                                                wrappedType="pipelines-authorization-component"
                                            />
                                        )}
                                        
                                        <WrappedComponent<IBranchesProps>
                                            loadingComponent={renderLoading}
                                            dependencies={["ms.vss-code-web.settings-branches"]}
                                            wrappedProps={{
                                                projectId: this.svc.getProject().id!,
                                                repositoryId: props.selected!.id,
                                                onRowActivate: this.onRowActivate,
                                                cardTitle: Resources.SecurityTitleBranch,
                                                cardDescription: Resources.PermissionsDescBranch,
                                                additionalData: permissionsAdditionalData
                                            }}
                                            wrappedType="settings-branches-component"
                                        />
                                    </>
                                )}
                            </>
                        );
                    }}
                </Observer>
            </div>
        );
    };

    private onRowActivate = (ref: SimpleGitRef) => {
        this.svc.setSelectedRefs(ref.ref);
        this.props.createChildLayer && this.props.createChildLayer();
    };
}

const enum SecurityResourceType {
    Unspecified = "Unspecified",
    Collection = "Collection",
    Project = "Project",
    Repo = "Repo",
    Ref = "Ref",
    ProjectGit = "ProjectGit",
    Tfvc = "Tfvc",
    Release = "Release"
}

function getSecurityComponentProps(permissionSet: IPermissionsSet, projectId: string, repo?: IRepository, ref?: string) {
    let resourceId: string;
    let resourceType = SecurityResourceType.ProjectGit;
    let resourceName: string | undefined;

    resourceId = projectId;
    if (repo && !repo.isTfvc) {
        resourceName = repo.name;
        resourceId += "/" + repo.id;
        resourceType = SecurityResourceType.Repo;
        if (ref) {
            resourceId += "/" + ref;
            resourceType = SecurityResourceType.Ref;
        }
    } else if (repo && repo.isTfvc) {
        resourceId = repo.name;
        resourceType = SecurityResourceType.Tfvc;
    }

    return {
        securityViewTitle: Resources.SecurityUserPermissionsTitle,
        permissionSetId: permissionSet.id,
        permissionSetToken: permissionSet.token,
        resourceId: resourceId,
        resourceType: resourceType,
        resourceName: resourceName
    };
}

function renderLoading() {
    return <Spinner size={SpinnerSize.large} />;
}
